// ── 타입 ──────────────────────────────────────────────────
export interface RoomInfo {
  roomCode:      string
  gameType:      string
  options:       Record<string, unknown>
  hostUserCode:  number
  isPrivate:     boolean
  maxSeats:      number
  currentSeats:  number   // 플레이석 인원 (SEQ=10072 STEP B: 옵저버 제외)
  observerCount: number   // SEQ=10072 STEP B — 옵저버 인원
  status:        string
}

// SEQ=10072 STEP B — 좌석 타입
export type SeatType = 'PLAYER' | 'OBSERVER'

export interface RoomMember {
  userCode:  number
  nickname:  string
  seatIndex: number
  seatType:  SeatType   // SEQ=10072 STEP B
  isHost:    boolean
  joinedAt:  string
}

export interface ChatEntry {
  userCode:  number | null  // null = 시스템 메시지
  nickname:  string | null
  message:   string
  timestamp: string
  isSystem:  boolean
}

/**
 * 시스템 메시지 카테고리 — 2026-05-09 화이트리스트 정책 도입.
 * 2026-05-06 전체 차단 정책 → 핵심 이벤트만 통과로 전환 (효담 결정 SEQ=10021).
 *
 * 통과 (노출):
 *   - TRADE_PLAYER_COMPLETE / TRADE_CANCEL — 플레이어 간 거래 성사·취소
 *   - ROBBER_MOVE / ROBBER_STEAL — 도적 이동·자원 약탈
 *   - DEVCARD_PLAYED — 발전 카드 사용 효과 (KNIGHT/ROAD_BUILDING/MONOPOLY/YEAR_OF_PLENTY)
 *   - LONGEST_ROAD / LARGEST_ARMY — 보너스 획득·이전
 *   - GAME_END — 게임 종료 (승자)
 *   - MEMBER_ENTER / MEMBER_LEAVE / HOST_CHANGE / ROOM_DELETED / GAME_START — 방 라이프사이클
 *
 * 차단 (drop):
 *   - TURN_CHANGE — 일반 턴 전환
 *   - DICE_ROLL — 일반 주사위 (총합·자원 분배)
 *   - BUILD_GAME — 본 턴 도로/마을/도시 건설
 *   - TRADE_PROPOSE / TRADE_RESPOND — 거래 제안·중간 응답 (REJECT/COUNTER 단건)
 *   - TRADE_BANK / TRADE_PORT — 4:1 / 2:1·3:1 거래
 *   - DEVCARD_BUY — 발전 카드 단순 구입 (사용 외)
 *   - 미지정 / undefined — 안전 default = drop
 */
export type SystemChatCategory =
  // 통과
  | 'TRADE_PLAYER_COMPLETE' | 'TRADE_CANCEL'
  | 'ROBBER_MOVE' | 'ROBBER_STEAL'
  | 'DEVCARD_PLAYED'
  | 'LONGEST_ROAD' | 'LARGEST_ARMY'
  | 'GAME_END'
  | 'MEMBER_ENTER' | 'MEMBER_LEAVE' | 'HOST_CHANGE' | 'ROOM_DELETED' | 'GAME_START'
  // SEQ=10114 P8-CK-C — 통과 (사용자 컨펌 #7 핵심 5종 중 야만인 침공 2종)
  | 'CK_BARBARIAN_INVADE_WIN' | 'CK_BARBARIAN_INVADE_LOSE'
  // SEQ=10115 P8-CK-D — 통과 (사용자 컨펌 #7 핵심 5종 중 진보 카드 사용)
  | 'CK_PROGRESS_USE'
  // SEQ=10112 P8-CK-A — 통과 (사용자 컨펌 #7 핵심 5종 중 도시 개선)
  | 'CK_CITY_IMPROVE' | 'CK_METROPOLIS_CHANGE'
  // 차단
  | 'TURN_CHANGE' | 'DICE_ROLL' | 'BUILD_GAME'
  | 'TRADE_PROPOSE' | 'TRADE_RESPOND' | 'TRADE_BANK' | 'TRADE_PORT'
  | 'DEVCARD_BUY'
  // SEQ=10114 P8-CK-C — 차단 (보드 트랙으로 시각화)
  | 'CK_BARBARIAN_MOVE'

const SYSTEM_CHAT_PASS: ReadonlySet<SystemChatCategory> = new Set<SystemChatCategory>([
  'TRADE_PLAYER_COMPLETE', 'TRADE_CANCEL',
  'ROBBER_MOVE', 'ROBBER_STEAL',
  'DEVCARD_PLAYED',
  'LONGEST_ROAD', 'LARGEST_ARMY',
  'GAME_END',
  'MEMBER_ENTER', 'MEMBER_LEAVE', 'HOST_CHANGE', 'ROOM_DELETED', 'GAME_START',
  // SEQ=10114 P8-CK-C — 핵심 5종 야만인 침공
  'CK_BARBARIAN_INVADE_WIN', 'CK_BARBARIAN_INVADE_LOSE',
  // SEQ=10115 P8-CK-D — 핵심 5종 진보 카드 사용
  'CK_PROGRESS_USE',
  // SEQ=10112 P8-CK-A — 핵심 5종 도시 개선 + 메트로폴리스 (백fill: P8-CK-A에서 추가 못 됨)
  'CK_CITY_IMPROVE', 'CK_METROPOLIS_CHANGE',
])

// ── 내부 상태 (단순 객체, React state로 주입 패턴) ──────────
let _room: RoomInfo | null = null
let _members: RoomMember[] = []
let _chatLog: ChatEntry[]  = []

type Listener = () => void
const _listeners: Set<Listener> = new Set()

function _notify() {
  _listeners.forEach(fn => fn())
}

// ── 구독 ─────────────────────────────────────────────────
export function subscribeRoom(fn: Listener) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

// ── 조회 ─────────────────────────────────────────────────
export const roomStore = {
  getRoom():    RoomInfo | null { return _room },
  getMembers(): RoomMember[]   { return _members },
  getChatLog(): ChatEntry[]    { return [..._chatLog] },

  // ── 설정 ────────────────────────────────────────────────
  setRoom(info: RoomInfo) {
    _room = { ...info }
    _notify()
  },

  // RoomDetail 동기화 (서버 응답 원본 객체 → 정규화)
  // ASP.NET Core 기본 직렬화 = camelCase. PascalCase / UPPER_CASE도 안전망으로 매칭.
  applyDetail(raw: {
    room: {
      roomCode?: string; RoomCode?: string; ROOM_CODE?: string
      gameType?: string; GameType?: string; GAME_TYPE?: string
      optionsJson?: string | null; OptionsJson?: string | null; OPTIONS_JSON?: string | null
      hostUserCode?: number; HostUserCode?: number; HOST_USER_CODE?: number
      isPrivate?: boolean | number; IsPrivate?: boolean | number; IS_PRIVATE?: boolean | number
      maxSeats?: number; MaxSeats?: number; MAX_SEATS?: number
      currentSeats?: number; CurrentSeats?: number; CURRENT_SEATS?: number
      observerCount?: number; ObserverCount?: number; OBSERVER_COUNT?: number
      status?: string; Status?: string; STATUS?: string
    }
    members: Array<{
      userCode?: number; UserCode?: number; USER_CODE?: number
      nickname?: string; Nickname?: string; NICKNAME?: string
      seatIndex?: number; SeatIndex?: number; SEAT_INDEX?: number
      seatType?: string; SeatType?: string; SEAT_TYPE?: string
      isHost?: boolean | number; IsHost?: boolean | number; IS_HOST?: boolean | number
      joinedAt?: string; JoinedAt?: string; JOINED_AT?: string
    }>
  }) {
    const r = raw.room
    _room = {
      roomCode:      (r.roomCode ?? r.RoomCode ?? r.ROOM_CODE ?? '') as string,
      gameType:      (r.gameType ?? r.GameType ?? r.GAME_TYPE ?? 'CATAN') as string,
      options:       parseOptions(r.optionsJson ?? r.OptionsJson ?? r.OPTIONS_JSON ?? null),
      hostUserCode:  Number(r.hostUserCode ?? r.HostUserCode ?? r.HOST_USER_CODE ?? 0),
      isPrivate:     !!(r.isPrivate ?? r.IsPrivate ?? r.IS_PRIVATE),
      maxSeats:      Number(r.maxSeats ?? r.MaxSeats ?? r.MAX_SEATS ?? 4),
      currentSeats:  Number(r.currentSeats ?? r.CurrentSeats ?? r.CURRENT_SEATS ?? 0),
      observerCount: Number(r.observerCount ?? r.ObserverCount ?? r.OBSERVER_COUNT ?? 0),
      status:        (r.status ?? r.Status ?? r.STATUS ?? 'WAITING') as string,
    }
    _members = raw.members.map(m => ({
      userCode:  Number(m.userCode ?? m.UserCode ?? m.USER_CODE ?? 0),
      nickname:  String(m.nickname ?? m.Nickname ?? m.NICKNAME ?? ''),
      seatIndex: Number(m.seatIndex ?? m.SeatIndex ?? m.SEAT_INDEX ?? 0),
      seatType:  normalizeSeatType(m.seatType ?? m.SeatType ?? m.SEAT_TYPE),
      isHost:    !!(m.isHost ?? m.IsHost ?? m.IS_HOST),
      joinedAt:  String(m.joinedAt ?? m.JoinedAt ?? m.JOINED_AT ?? ''),
    }))
    _notify()
  },

  // SeatChanged 브로드캐스트 적용 — 멤버 좌석 타입/인덱스 갱신 (STEP B)
  updateSeat(userCode: number, seatType: SeatType, seatIndex: number) {
    _members = _members.map(m =>
      m.userCode === userCode ? { ...m, seatType, seatIndex } : m
    )
    if (_room) {
      const playerCount = _members.filter(m => m.seatType === 'PLAYER').length
      const observerCount = _members.filter(m => m.seatType === 'OBSERVER').length
      _room = { ..._room, currentSeats: playerCount, observerCount }
    }
    _notify()
  },

  addMember(member: RoomMember) {
    const exists = _members.find(m => m.userCode === member.userCode)
    if (!exists) {
      _members = [..._members, member]
    }
    if (_room) {
      // STEP B — currentSeats = 플레이석만, observerCount = 옵저버만
      _room = {
        ..._room,
        currentSeats:  _members.filter(m => m.seatType === 'PLAYER').length,
        observerCount: _members.filter(m => m.seatType === 'OBSERVER').length,
      }
    }
    _notify()
  },

  removeMember(userCode: number) {
    _members = _members.filter(m => m.userCode !== userCode)
    if (_room) {
      _room = {
        ..._room,
        currentSeats:  _members.filter(m => m.seatType === 'PLAYER').length,
        observerCount: _members.filter(m => m.seatType === 'OBSERVER').length,
      }
    }
    _notify()
  },

  updateHost(newHostUserCode: number) {
    _members = _members.map(m => ({ ...m, isHost: m.userCode === newHostUserCode }))
    if (_room) {
      _room = { ..._room, hostUserCode: newHostUserCode }
    }
    _notify()
  },

  appendChat(entry: ChatEntry) {
    // 일반(유저 간) 채팅 + 시스템 메시지(화이트리스트 통과분) 모두 누적.
    // 시스템 emit은 appendSystemChat()에서 카테고리 필터링 후 본 함수를 호출 (entry.isSystem=true).
    _chatLog = [..._chatLog, entry]
    // 메모리 한계 (최근 200개)
    if (_chatLog.length > 200) _chatLog = _chatLog.slice(-200)
    _notify()
  },

  /**
   * 시스템 메시지 push 진입점 (입퇴장·턴·주사위·건설·도적·약탈·거래·게임 종료 등).
   * 2026-05-09 화이트리스트 정책 — category 인자 필수, SYSTEM_CHAT_PASS 통과분만 chatLog 누적.
   * 차단 카테고리 또는 미지정 호출은 silently drop (정책 토글 시 SYSTEM_CHAT_PASS 수정만으로 즉시 반영).
   *
   * @param message 표시 텍스트
   * @param category 이벤트 카테고리 (SystemChatCategory 참조)
   */
  appendSystemChat(message: string, category?: SystemChatCategory) {
    if (!category || !SYSTEM_CHAT_PASS.has(category)) return  // 차단
    // 통과 — appendChat 경유로 chatLog 누적 (isSystem=true)
    const now = new Date()
    const hh = String(now.getHours()).padStart(2, '0')
    const mm = String(now.getMinutes()).padStart(2, '0')
    this.appendChat({
      userCode:  null,
      nickname:  null,
      message,
      timestamp: `${hh}:${mm}`,
      isSystem:  true,
    })
  },

  clearRoom() {
    _room    = null
    _members = []
    _chatLog = []
    _notify()
  },

  /**
   * SEQ=10110 STEP=4 — 본인 화면 채팅만 클리어 (서버 무호출, DB 보존, 룸/멤버는 그대로).
   * Room.tsx / Play.tsx 의 '지우기' 버튼에서 호출.
   */
  clearChatLog() {
    _chatLog = []
    _notify()
  },
}

// ── helpers ───────────────────────────────────────────────
function parseOptions(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) return {}
  try { return JSON.parse(raw) } catch { return {} }
}

// SEQ=10072 STEP B — 좌석 타입 정규화 (서버 누락 시 PLAYER 기본 — 하위호환)
function normalizeSeatType(raw: string | null | undefined): SeatType {
  return String(raw ?? '').toUpperCase() === 'OBSERVER' ? 'OBSERVER' : 'PLAYER'
}
